/**
 * Subscribe to a Server-Sent Events stream.
 *
 * Used by the run monitor. SSE rather than WebSockets (ADR-0035): the monitor has
 * nothing to send upstream, SSE is plain HTTP, it survives proxies that do not
 * understand upgrade negotiation, and it reconnects on its own.
 *
 * **`EventSource` cannot carry an Authorization header**, and the access token is held in
 * memory rather than in a cookie, so the credential every other request uses cannot reach
 * this one endpoint. This hook previously opened the stream with `withCredentials` and a
 * comment claiming a session cookie covered it. There is no session cookie — the only one
 * is the refresh token, scoped to `/api/v1/auth` — so every stream arrived
 * unauthenticated, the server answered 401, and the monitor showed "Connection
 * interrupted; retrying." forever. Watching a run had never worked in a browser.
 *
 * So: mint a single-use ticket over an ordinary authenticated `fetch`, then open the
 * stream with it. The ticket is spent on redemption and expires in seconds, which is what
 * makes it safe in a URL where an access token would not be.
 *
 * **The stream's frames are named**, and named frames do not reach `onmessage`. The
 * server sends `event: run.state` and `event: run.finished`; `EventSource` routes those to
 * listeners registered for those names and delivers only *unnamed* frames to `onmessage`.
 * This hook listened on `onmessage` alone, so once the 401 above was fixed the monitor
 * would have connected and then displayed nothing at all — a second defect in the same
 * feature, hidden behind the first. Callers name the events they expect.
 */

import { useEffect, useRef, useState } from 'react';
import { request } from '@/lib/api';

export interface SSEState<T> {
  /** The most recent message, parsed. */
  last: T | null;
  /** Every message received, in order. */
  events: T[];
  /** Whether the stream is currently open. */
  open: boolean;
  /** The last transport error, if any. */
  error: string | null;
}

interface StreamTicket {
  ticket: string;
  expires_in_seconds: number;
}

/**
 * Open an SSE stream and collect its messages.
 *
 * @param path - API path of the stream without the `/api/v1` prefix, e.g.
 *   `/runs/{id}/events`, or `null` to stay closed.
 * @param eventNames - Named frames to subscribe to. Unnamed frames always arrive.
 * @returns The stream state.
 */
export function useSSE<T>(path: string | null, eventNames: readonly string[] = []): SSEState<T> {
  // Joined to a stable string before it reaches the dependency array: a caller writing
  // the array inline gets a new identity on every render, which would tear down and
  // reopen the stream — minting a fresh ticket each time — for as long as it is watched.
  const names = eventNames.join(',');
  const [events, setEvents] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!path) return undefined;

    // The effect may be torn down while the ticket request is still in flight — the
    // analyst clicked away, or React re-ran it. Without this, the stream opens after the
    // cleanup has run and is never closed, leaking a connection per click.
    let cancelled = false;
    setEvents([]);
    setOpen(false);
    setError(null);

    // `/runs/{id}/events` -> `/runs/{id}/stream-ticket`. Derived rather than passed so a
    // caller cannot open a stream and mint a ticket for two different runs.
    const ticketPath = path.replace(/\/events$/, '/stream-ticket');

    void request<StreamTicket>(ticketPath, { method: 'POST' })
      .then(({ ticket }) => {
        if (cancelled) return;

        const url = `/api/v1${path}?ticket=${encodeURIComponent(ticket)}`;
        const source = new EventSource(url);
        sourceRef.current = source;
        setOpen(true);

        const receive = (message: MessageEvent<string>) => {
          try {
            setEvents((previous) => [...previous, JSON.parse(message.data) as T]);
          } catch {
            // A malformed frame is dropped rather than thrown: one bad message must not
            // close a monitor an analyst is watching a long run through.
            setError('A malformed event was received and ignored.');
          }
        };

        source.onmessage = receive;
        for (const name of names.split(',').filter(Boolean)) {
          source.addEventListener(name, receive as EventListener);
        }

        source.onerror = () => {
          // **The ticket is spent, so the browser's own reconnection cannot work** — it
          // would replay the same URL and be refused. Close deliberately and say so,
          // rather than let EventSource retry a URL that can never succeed again and
          // report a transient-sounding error forever.
          source.close();
          sourceRef.current = null;
          setOpen(false);
          setError('The connection to this run ended. Reopen the monitor to continue.');
        };
      })
      .catch(() => {
        if (cancelled) return;
        setOpen(false);
        setError('Could not open the run monitor. You may not have access to this run.');
      });

    return () => {
      cancelled = true;
      sourceRef.current?.close();
      sourceRef.current = null;
      setOpen(false);
    };
  }, [path, names]);

  return { last: events.length ? (events[events.length - 1] as T) : null, events, open, error };
}
